/** Tipos de layout suportados pelo renderizador ARCA */
export const SLIDE_LAYOUTS = {
  TITULO_CENTRO: 'titulo-centro',
  TITULO_BULLETS: 'titulo-bullets',
  DUAS_COLUNAS: 'duas-colunas',
};

export const LAYOUT_LABELS = {
  [SLIDE_LAYOUTS.TITULO_CENTRO]: 'Título central',
  [SLIDE_LAYOUTS.TITULO_BULLETS]: 'Título + bullets',
  [SLIDE_LAYOUTS.DUAS_COLUNAS]: 'Duas colunas',
};

export const CHART_TYPES = ['bar', 'line', 'pie', 'doughnut'];

export const CHART_COLORS = ['#38bdf8', '#34d399', '#fbbf24', '#a78bfa', '#f87171', '#94a3b8'];

export const FONT_OPTIONS = [
  { id: 'system', label: 'Sistema (Segoe UI)', value: '"Segoe UI", system-ui, sans-serif' },
  { id: 'inter', label: 'Inter', value: 'Inter, "Segoe UI", sans-serif' },
  { id: 'georgia', label: 'Georgia', value: 'Georgia, "Times New Roman", serif' },
  { id: 'roboto', label: 'Roboto', value: 'Roboto, "Helvetica Neue", sans-serif' },
  { id: 'mono', label: 'Consolas', value: 'Consolas, "Courier New", monospace' },
];

export const DEFAULT_TIPOGRAFIA = {
  fontFamily: FONT_OPTIONS[0].value,
  tituloSize: 28,
  subtituloSize: 16,
  bulletSize: 15,
  boldTitulo: true,
  align: 'left',
};

export function normalizeTipografia(tipografia) {
  return {
    ...DEFAULT_TIPOGRAFIA,
    ...(tipografia && typeof tipografia === 'object' ? tipografia : {}),
  };
}

/** @returns {object} */
export function createEmptySlide() {
  return {
    id: `slide_${Date.now()}`,
    titulo: '',
    subtitulo: '',
    bullets: [''],
    layout: SLIDE_LAYOUTS.TITULO_BULLETS,
    midias: [],
    graficos: [],
    theme: 'cyber-hud',
    tipografia: { ...DEFAULT_TIPOGRAFIA },
  };
}

export function normalizeGrafico(grafico, index = 0) {
  if (!grafico) return null;
  const labels = Array.isArray(grafico.labels) ? grafico.labels.map((l) => String(l)) : [];
  const valores = Array.isArray(grafico.valores)
    ? grafico.valores.map((v) => Number(v)).filter((n) => !Number.isNaN(n))
    : [];
  if (!labels.length || !valores.length) return null;
  const len = Math.min(labels.length, valores.length);
  return {
    id: grafico.id || `chart_${Date.now()}_${index}`,
    titulo: String(grafico.titulo || 'Gráfico').trim(),
    tipo: CHART_TYPES.includes(grafico.tipo) ? grafico.tipo : 'bar',
    labels: labels.slice(0, len),
    valores: valores.slice(0, len),
  };
}

export function normalizeMidia(midia, index = 0) {
  if (!midia) return null;
  const w = Number(midia.w ?? midia.largura) || 32;
  return {
    ...midia,
    id: midia.id || `media_${Date.now()}_${index}`,
    nome: midia.nome || 'Imagem',
    w,
    largura: w,
    x: midia.x != null ? Number(midia.x) : 8 + (index % 3) * 28,
    y: midia.y != null ? Number(midia.y) : 28,
    zIndex: Number(midia.zIndex) || index + 1,
    fonte: midia.fonte || 'upload',
    licenca: midia.licenca || null,
  };
}

/** Garante estrutura mínima do slide após API ou importação. */
export function normalizeSlide(slide) {
  if (!slide) return null;
  const base = createEmptySlide();
  return {
    ...base,
    ...slide,
    id: slide.id || base.id,
    bullets:
      Array.isArray(slide.bullets) && slide.bullets.length
        ? slide.bullets.map((b) => String(b))
        : base.bullets,
    midias: Array.isArray(slide.midias)
      ? slide.midias.map(normalizeMidia).filter(Boolean)
      : [],
    graficos: Array.isArray(slide.graficos)
      ? slide.graficos.map(normalizeGrafico).filter(Boolean)
      : [],
    theme: slide.theme || 'cyber-hud',
    tipografia: normalizeTipografia(slide.tipografia),
  };
}

export function duplicateSlide(slide) {
  const copy = normalizeSlide(slide);
  if (!copy) return createEmptySlide();
  return {
    ...copy,
    id: `slide_${Date.now()}`,
    midias: (copy.midias || []).map((m) => ({
      ...m,
      id: `media_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
    })),
    graficos: (copy.graficos || []).map((g, i) => ({
      ...g,
      id: `chart_${Date.now()}_${i}`,
    })),
  };
}

/**
 * Geração local mock (legado).
 */
export function mockSlideFromPrompt(prompt, modo = 'livre') {
  const text = String(prompt || '').trim();
  const lines = text
    .split(/\n+/)
    .map((l) => l.trim())
    .filter(Boolean);

  const titulo = lines[0] || 'Nova apresentação';
  const subtitulo =
    modo === 'conectado'
      ? 'Contexto agregado do SGC'
      : lines[1] || 'Slide gerado a partir do seu prompt';

  const bulletSource = lines.slice(modo === 'conectado' ? 1 : 2);
  let bullets =
    bulletSource.length > 0
      ? bulletSource
      : [
          'Ponto principal da apresentação',
          'Detalhe ou evidência de apoio',
          'Próximo passo ou conclusão',
        ];

  if (modo === 'conectado') {
    bullets = ['Resumo de contratos ativos na unidade', ...bullets.slice(0, 2)];
  }

  const layout =
    bullets.length >= 4
      ? SLIDE_LAYOUTS.DUAS_COLUNAS
      : lines.length <= 1
        ? SLIDE_LAYOUTS.TITULO_CENTRO
        : SLIDE_LAYOUTS.TITULO_BULLETS;

  return normalizeSlide({
    titulo,
    subtitulo,
    bullets,
    layout,
    midias: [],
    meta: { modo, geradoEm: new Date().toISOString(), fonte: 'mock' },
  });
}
